import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import { twMerge } from 'tailwind-merge';
import clsx from 'clsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import Indent from './indent';
import SvgBold from '@icons/editor/bold.svg?react';
import SvgItalic from '@icons/editor/italic.svg?react';
import SvgUnderline from '@icons/editor/underline.svg?react';
import SvgStrikethrough from '@icons/editor/strikethrough.svg?react';
import SvgList from '@icons/editor/list.svg?react';
import SvgIndentLeft from '@icons/editor/indent-left.svg?react';
import SvgIndentRight from '@icons/editor/indent-right.svg?react';
import SvgAlignLeft from '@icons/editor/align-left.svg?react';
import SvgAlignCenter from '@icons/editor/align-center.svg?react';
import SvgAlignRight from '@icons/editor/align-right.svg?react';
import SvgRemoveStyle from '@icons/editor/removestyle.svg?react';
import SvgMaximize from '@icons/editor/maximize.svg?react';

interface RichTextEditorProps {
  value?: string;
  onChange: (html: string) => void;
  disabled?: boolean;
  maxLength?: number;
  allowCodeBlock?: boolean;
  dataTestId?: string;
  /** Put the caret at the end on mount. Only for hosts that mount the editor when edit mode opens. */
  autoFocus?: boolean;
}

// @tiptap/core is only a transitive dependency, so derive the Editor type from the hook.
type TiptapEditor = NonNullable<ReturnType<typeof useEditor>>;

// TipTap returns '<p></p>' for an empty document; treat that as an empty value.
const isEmptyHtml = (html: string): boolean => !html || html === '<p></p>';

interface ToolButtonProps {
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  title: string;
  icon: React.ReactNode;
}

// Flat icon button matching the Figma WYSIWYG toolbar (21px glyph, secondary by default,
// brand when active/hover). mousedown is swallowed so the editor keeps its selection.
const ToolButton: React.FC<ToolButtonProps> = ({ active, disabled, onClick, title, icon }) => (
  <button
    type={'button'}
    title={title}
    disabled={disabled}
    onMouseDown={e => e.preventDefault()}
    onClick={onClick}
    className={twMerge(
      clsx(
        'flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded bg-transparent p-0 transition-colors',
        // Active state highlights only the icon (brand). Hover highlights only the background — the
        // two are independent so an active button stays recognizable while hovered.
        active ? 'text-content-brand' : 'text-content-secondary',
        'cursor-pointer enabled:hover:bg-[#F2F4F6] disabled:cursor-not-allowed disabled:opacity-40',
      ),
    )}>
    {icon}
  </button>
);

const ToolbarDivider: React.FC = () => <span className={'h-[21px] w-px shrink-0 bg-[#E7EBEE]'} />;

const RichTextEditor: React.FC<RichTextEditorProps> = ({ value, onChange, disabled = false, maxLength, allowCodeBlock = true, dataTestId, autoFocus = false }) => {
  const { tr } = useTranslationLgs();
  // `expanded` keeps the editor out of the inline slot; `overlayVisible` drives the overlay's
  // enter/exit animation. On close we only flip `overlayVisible`, then drop `expanded` once the
  // exit animation finishes — so the single editor instance is never mounted in both places.
  const [expanded, setExpanded] = useState(false);
  const [overlayVisible, setOverlayVisible] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  const openOverlay = () => {
    setExpanded(true);
    setOverlayVisible(true);
  };
  const closeOverlay = () => setOverlayVisible(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        ...(allowCodeBlock ? {} : { codeBlock: false }),
        // Link is disabled everywhere: the only editing UI was a window.prompt (broken for no-selection,
        // poor UX). To re-enable, set link back to { openOnClick: false, autolink: true }, restore the
        // toolbar Link button, and replace setLink's window.prompt with a proper in-editor link popover.
        link: false,
      }),
      TextAlign.configure({ types: ['paragraph'] }),
      Indent.configure({ types: ['paragraph'] }),
    ],
    content: value || '',
    editable: !disabled,
    immediatelyRender: false,
    // Re-render the toolbar on every transaction so active states / char count stay in sync with
    // the cursor (defaults to false in @tiptap/react v3).
    shouldRerenderOnTransaction: true,
    onUpdate: ({ editor }: { editor: TiptapEditor }) => {
      const html = editor.getHTML();
      onChange(isEmptyHtml(html) ? '' : html);
    },
    editorProps: {
      attributes: {
        class: twMerge(
          'min-h-full px-3 py-2 outline-none text-sm leading-normal',
          '[&_p]:m-0 [&_p]:mb-2 [&_p:last-child]:mb-0',
          '[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-0 [&_ul]:mb-2',
          '[&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-0 [&_ol]:mb-2',
          '[&_a]:text-content-brand [&_a]:underline',
        ),
      },
    },
  });

  // Sync external value changes (editing an existing record, form reset) into the editor
  // without re-emitting onChange.
  useEffect(() => {
    if (!editor) return;
    const next = value || '';
    const current = editor.getHTML();
    if (next !== current && !(isEmptyHtml(next) && isEmptyHtml(current))) {
      editor.commands.setContent(next, { emitUpdate: false });
    }
  }, [value, editor]);

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [disabled, editor]);

  // When the expanded overlay opens, drop the caret onto the last editable row so the user can
  // start typing immediately — matching the gutter-click behaviour of the inline view. EditorContent
  // remounts into the portal when the overlay appears, so defer to the next frame to let the editor
  // DOM re-attach before we focus it.
  useEffect(() => {
    if (!overlayVisible || disabled || !editor) return;
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => editor.chain().focus('end').run());
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [overlayVisible, disabled, editor]);

  // Opt-in autofocus for hosts that mount the editor only when the user opens edit mode (a note tag's
  // pane, an inline comment edit form): place the caret on the last editable row so they can type
  // straight away. Deliberately NOT tied to visibility — an editor that merely scrolls into view must
  // never grab focus, because focusing scrolls every scrollable ancestor (including overflow:hidden
  // ones) and can leave the page wedged in a state the user cannot scroll out of.
  useEffect(() => {
    if (!autoFocus || disabled || !editor) return;
    editor.chain().focus('end', { scrollIntoView: false }).run();
  }, [autoFocus, disabled, editor]);

  // The expanded overlay is a modal portaled to <body>. Stop its mousedown from bubbling to
  // document so host popups that close on an outside mousedown (e.g. a ButtonTag pane) don't
  // collapse — and discard the edit — when the user interacts with or closes the overlay.
  useEffect(() => {
    const el = overlayRef.current;
    if (!el) return;
    const stop = (e: MouseEvent) => e.stopPropagation();
    el.addEventListener('mousedown', stop);
    return () => el.removeEventListener('mousedown', stop);
  }, [overlayVisible]);

  // Close the expanded overlay on Escape.
  useEffect(() => {
    if (!overlayVisible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOverlayVisible(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [overlayVisible]);

  if (!editor) return null;

  const iconClass = 'h-[21px] w-[21px]';

  const toolbar = (
    <div className={'flex flex-wrap items-center gap-[8px] rounded-[4px] border border-solid border-[#E7EBEE] bg-white p-[8px]'}>
      <div className={'flex items-center gap-[12px]'}>
        <ToolButton title={tr('RichTextEditor.bold', 'Bold')} active={editor.isActive('bold')} disabled={disabled} onClick={() => editor.chain().focus().toggleBold().run()} icon={<SvgBold className={iconClass} />} />
        <ToolButton title={tr('RichTextEditor.italic', 'Italic')} active={editor.isActive('italic')} disabled={disabled} onClick={() => editor.chain().focus().toggleItalic().run()} icon={<SvgItalic className={iconClass} />} />
        <ToolButton title={tr('RichTextEditor.underline', 'Underline')} active={editor.isActive('underline')} disabled={disabled} onClick={() => editor.chain().focus().toggleUnderline().run()} icon={<SvgUnderline className={iconClass} />} />
        <ToolButton title={tr('RichTextEditor.strikethrough', 'Strikethrough')} active={editor.isActive('strike')} disabled={disabled} onClick={() => editor.chain().focus().toggleStrike().run()} icon={<SvgStrikethrough className={iconClass} />} />
      </div>
      <ToolbarDivider />
      <div className={'flex items-center gap-[12px]'}>
        <ToolButton title={tr('RichTextEditor.list', 'List')} active={editor.isActive('bulletList')} disabled={disabled} onClick={() => editor.chain().focus().toggleBulletList().run()} icon={<SvgList className={iconClass} />} />
        <ToolButton title={tr('RichTextEditor.decreaseIndent', 'Decrease indent')} disabled={disabled} onClick={() => editor.chain().focus().outdent().run()} icon={<SvgIndentLeft className={iconClass} />} />
        <ToolButton title={tr('RichTextEditor.increaseIndent', 'Increase indent')} disabled={disabled} onClick={() => editor.chain().focus().indent().run()} icon={<SvgIndentRight className={iconClass} />} />
      </div>
      <ToolbarDivider />
      <div className={'flex items-center gap-[12px]'}>
        <ToolButton title={tr('RichTextEditor.alignLeft', 'Align left')} active={editor.isActive({ textAlign: 'left' })} disabled={disabled} onClick={() => editor.chain().focus().setTextAlign('left').run()} icon={<SvgAlignLeft className={iconClass} />} />
        <ToolButton title={tr('RichTextEditor.alignCenter', 'Align center')} active={editor.isActive({ textAlign: 'center' })} disabled={disabled} onClick={() => editor.chain().focus().setTextAlign('center').run()} icon={<SvgAlignCenter className={iconClass} />} />
        <ToolButton title={tr('RichTextEditor.alignRight', 'Align right')} active={editor.isActive({ textAlign: 'right' })} disabled={disabled} onClick={() => editor.chain().focus().setTextAlign('right').run()} icon={<SvgAlignRight className={iconClass} />} />
        <ToolButton title={tr('RichTextEditor.removeFormatting', 'Remove formatting')} disabled={disabled} onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()} icon={<SvgRemoveStyle className={iconClass} />} />
      </div>
      <div className={'ml-auto flex items-center gap-[12px]'}>
        <ToolButton
          title={overlayVisible ? tr('RichTextEditor.collapse', 'Collapse') : tr('RichTextEditor.expand', 'Expand')}
          active={overlayVisible}
          onClick={() => (overlayVisible ? closeOverlay() : openOverlay())}
          icon={<SvgMaximize className={iconClass} />}
        />
      </div>
    </div>
  );

  const charCount = editor.getText().length;
  // maxLength is a soft, non-blocking guardrail (the note column is LONGTEXT); past it the counter
  // goes red + bold to nudge against very long notes, but typing is never capped.
  const overSoftLimit = maxLength != null && charCount > maxLength;
  const counter = (
    <div className={twMerge('text-right text-[10px]', overSoftLimit ? 'font-bold text-complementary-error' : 'text-content-secondary')}>
      {tr('RichTextEditor.charCount', '{{actual}} znaků', { actual: charCount })}
    </div>
  );

  // Clicking the empty area of the box (below the last line, or an empty editor) lands on the
  // wrapper rather than the ProseMirror content, so the browser never moves the caret. Redirect
  // those clicks to the end of the document so the cursor snaps to the last editable row.
  const focusEndOnGutterClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (disabled) return;
    // Only act when the click missed the editable content itself; real clicks inside the text let
    // ProseMirror place the caret normally.
    if ((e.target as HTMLElement).closest('.ProseMirror')) return;
    editor.chain().focus('end').run();
  };

  const contentBox = (extra: string) => (
    <div
      onMouseDown={focusEndOnGutterClick}
      data-testid={dataTestId}
      className={twMerge(clsx('w-full cursor-text overflow-y-auto rounded-[4px] border border-solid border-[#E7EBEE] bg-white', { 'cursor-default bg-content-tertiary': disabled }, extra))}>
      <EditorContent editor={editor} />
    </div>
  );

  return (
    <>
      <div ref={rootRef} className={'flex w-full flex-col gap-2'}>
        {!expanded && toolbar}
        {expanded ? (
          // Keep a placeholder inline so the form layout does not jump while editing in the overlay.
          <div className={'flex h-[160px] w-full items-center justify-center rounded-[4px] border border-dashed border-[#E7EBEE] text-sm text-content-secondary'}>
            {tr('RichTextEditor.editingInOverlay', 'Editing in expanded view…')}
          </div>
        ) : (
          contentBox('min-h-[160px] max-h-[320px]')
        )}
        {!expanded && counter}
      </div>
      {createPortal(
        <AnimatePresence onExitComplete={() => setExpanded(false)}>
          {overlayVisible && (
            <motion.div
              key={'rte-overlay'}
              ref={overlayRef}
              className={'fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 p-6'}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              onClick={closeOverlay}>
              <motion.div
                className={'flex h-[85vh] w-[min(900px,92vw)] flex-col gap-2 rounded-lg bg-white p-4 shadow-xl'}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.18, ease: 'easeOut' }}
                onClick={e => e.stopPropagation()}>
                {toolbar}
                {contentBox('flex-1')}
                {counter}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </>
  );
};

export default RichTextEditor;